/**
 * Chunky cartoon buttons: thick outline, a solid "ledge" underneath that the
 * button sinks into when pressed, and a springy release. Colours come from the
 * theme (button-bg / button-text, ui-panel / ui-text), which the theme packs
 * guarantee at ≥ 4.5:1 contrast.
 */
import type { ComponentPropsWithRef } from 'react';
import { Icon, type IconName } from './Icon.tsx';
import { Spinner } from './Spinner.tsx';
import './ui.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'play';
export type ButtonSize = 'md' | 'lg' | 'xl';

export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  /** Icon after the text (e.g. a chevron). */
  iconEnd?: IconName;
  /** Working: shows a spinner, disables the button and sets aria-busy. */
  busy?: boolean;
  /** Stretch to the container's width. */
  block?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconEnd,
  busy = false,
  block = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = ['ui-btn', `ui-btn--${variant}`, `ui-btn--${size}`, block && 'ui-btn--block', className]
    .filter(Boolean)
    .join(' ');
  return (
    <button {...rest} type={type} className={classes} disabled={disabled || busy} aria-busy={busy || undefined}>
      {busy ? <Spinner size="small" /> : icon && <Icon name={icon} className="ui-btn__icon" />}
      {children !== undefined && <span className="ui-btn__text">{children}</span>}
      {iconEnd && <Icon name={iconEnd} className="ui-btn__icon ui-btn__icon--end" />}
    </button>
  );
}

export interface IconButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
  icon: IconName;
  /** Accessible name (also shown as a tooltip). */
  label: string;
  variant?: 'secondary' | 'ghost';
}

/** A round, icon-only button (≥ 48 px). */
export function IconButton({ icon, label, variant = 'secondary', className, type = 'button', ...rest }: IconButtonProps) {
  const classes = ['ui-icon-btn', `ui-icon-btn--${variant}`, className].filter(Boolean).join(' ');
  return (
    <button {...rest} type={type} className={classes} aria-label={label} title={label}>
      <Icon name={icon} />
    </button>
  );
}
